import { useEffect, useMemo, useState } from 'react';
import { apiFetch } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import {
  resolveBrandTheme,
  resolvePortalTheme,
  type BrandThemeConfig,
  type PortalThemeTemplate,
} from '@/lib/portal-theme';

interface TenantThemePayload {
  name?: string;
  slug?: string;
  logoUrl?: string;
  portalThemeTemplate?: PortalThemeTemplate;
  brandTheme?: BrandThemeConfig;
}

interface UseBrandThemeConfigOptions {
  enabled?: boolean;
}

const tenantThemeCache = new Map<string, TenantThemePayload>();

export function useBrandThemeConfig(options: UseBrandThemeConfigOptions = {}) {
  const enabled = options.enabled ?? true;
  const { user } = useAuth();
  const cacheKey = user?.tenantId ?? 'anonymous';
  const [payload, setPayload] = useState<TenantThemePayload | undefined>(() => tenantThemeCache.get(cacheKey));
  const [loading, setLoading] = useState(enabled && Boolean(user) && !tenantThemeCache.has(cacheKey));

  useEffect(() => {
    if (!enabled || !user) {
      setLoading(false);
      return;
    }

    const cached = tenantThemeCache.get(cacheKey);
    if (cached) {
      setPayload(cached);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    apiFetch<TenantThemePayload>('/auth/tenant/company')
      .then((data) => {
        if (cancelled) return;
        tenantThemeCache.set(cacheKey, data);
        setPayload(data);
      })
      .catch(() => {
        if (!cancelled) setPayload(undefined);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [cacheKey, enabled, user]);

  const brandTheme = useMemo(
    () => resolveBrandTheme(payload?.portalThemeTemplate, payload?.brandTheme),
    [payload?.brandTheme, payload?.portalThemeTemplate],
  );

  const portalTheme = useMemo(
    () => resolvePortalTheme(payload?.portalThemeTemplate),
    [payload?.portalThemeTemplate],
  );

  return {
    brandTheme,
    portalTheme,
    companyBranding: {
      name: payload?.name,
      slug: payload?.slug,
      logoUrl: payload?.logoUrl,
    },
    loading,
  };
}
